import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate } from 'react-router-dom';
import { z } from 'zod';
import {
  criarTorneio,
  TorneioInput,
} from '../../services/torneioService';
import { StatusTorneio } from '../../utils/StatusTorneio';
import { toLocalDateTimeString } from '../../utils/date';

const tournamentSchema = z.object({
  nome: z.string().min(1, 'Nome é obrigatório'),
  dataInicio: z.string().min(1, 'Data de início é obrigatória'),
  dataFim: z.string().min(1, 'Data de fim é obrigatória'),
}).refine((data) => {
  if (!data.dataInicio || !data.dataFim) return true;
  return new Date(data.dataInicio) <= new Date(data.dataFim);
}, {
  message: 'Data de início não pode ser maior que a data de fim',
  path: ['dataInicio'],
});

interface CreateTournamentDialogProps {
  open: boolean;
  onClose: () => void;
  ligaId?: string | null;
  onSuccess?: () => void;
}

const CreateTournamentDialog: React.FC<CreateTournamentDialogProps> = ({
  open,
  onClose,
  ligaId,
  onSuccess,
}) => {
  const [nome, setNome] = useState('');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{
    nome?: string;
    dataInicio?: string;
    dataFim?: string;
  }>({});
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      setNome('');
      setDataInicio('');
      setDataFim('');
      setErrors({});
    }
  }, [open]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const validate = () => {
    const result = tournamentSchema.safeParse({ nome: nome.trim(), dataInicio, dataFim });
    if (!result.success) {
      const errs: { nome?: string; dataInicio?: string; dataFim?: string } = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as 'nome' | 'dataInicio' | 'dataFim';
        errs[field] = err.message;
      });
      setErrors(errs);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const input: TorneioInput = {
        nome: nome.trim(),
        dataInicio,
        dataFim,
        dataInscricaoInicio: dataInicio ? toLocalDateTimeString(dataInicio + 'T00:00:00') : null,
        dataInscricaoFim: dataFim ? toLocalDateTimeString(dataFim + 'T23:59:59') : null,
        status: StatusTorneio.AGENDADO,
      };
      if (ligaId) {
        (input as any).ligaId = ligaId;
      }
      const created = await criarTorneio(input);
      setSnackbar({
        open: true,
        message: 'Torneio criado com sucesso',
        severity: 'success',
      });
      onClose();
      if (onSuccess) {
        onSuccess();
      } else {
        navigate(`/admin/edittournament/${created.id}`);
      }
    } catch (err: any) {
      const msg = err?.message || 'Erro ao criar torneio.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () =>
    setSnackbar((s) => ({ ...s, open: false }));


  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="create-tournament-title"
      >
        <DialogTitle id="create-tournament-title">Criar Torneio</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {Object.keys(errors).length > 0 && (
              <Alert severity="error">
                <strong>Corrija os seguintes erros:</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: '20px' }}>
                  {errors.nome && <li>{errors.nome}</li>}
                  {errors.dataInicio && <li>{errors.dataInicio}</li>}
                  {errors.dataFim && <li>{errors.dataFim}</li>}
                </ul>
              </Alert>
            )}
            <TextField
              label="Nome do torneio"
              required
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              error={Boolean(errors.nome)}
              helperText={errors.nome}
              inputProps={{ 'data-testid': 'tournament-name' }}
            />
            <div>
              <label className="d-block mb-2" htmlFor="modal-data-inicio">
                Data de início
              </label>
              <DatePicker
                id="modal-data-inicio"
                selected={dataInicio ? new Date(dataInicio) : null}
                onChange={(date: Date | null) =>
                  setDataInicio(date ? date.toISOString().split('T')[0] : '')
                }
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="Selecione a data de início"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataInicio && (
                <div className="text-danger small">{errors.dataInicio}</div>
              )}
            </div>
            <div>
              <label className="d-block mb-2" htmlFor="modal-data-fim">
                Data de fim
              </label>
              <DatePicker
                id="modal-data-fim"
                selected={dataFim ? new Date(dataFim) : null}
                onChange={(date: Date | null) =>
                  setDataFim(date ? date.toISOString().split('T')[0] : '')
                }
                dateFormat="dd/MM/yyyy"
                className="form-control"
                placeholderText="Selecione a data de fim"
                popperProps={{ strategy: 'fixed' }}
              />
              {errors.dataFim && (
                <div className="text-danger small">{errors.dataFim}</div>
              )}
            </div>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            data-testid="tournament-create-submit"
          >
            {loading ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CreateTournamentDialog;

